import { AddressStore, addressStore } from '@entities/address'
import { clusterLib, clusterStore, ClusterStore } from '@entities/cluster'
import { MIN_PAINTER_SIZE, PainterStore, painterStore } from '@entities/painter'
import { PlanStore, planStore } from '@entities/plan'
import { SessionStore, sessionStore } from '@entities/session'
import { UsersStore, usersStore } from '@entities/user'
import { Action } from '@reduxjs/toolkit'
import { NavbarStore, navbarStore } from '@shared/layouts/navbar'
import { registerEpic } from '@shared/lib/redux'
import { RouterStore, routerStore } from '@shared/lib/router'
import { AppStore, appStore } from '@shared/model'
import {
  MobileSearchStore,
  searchPannelMobileStore
} from '@widgets/search-pannel-mobile'
import { StateObservable } from 'redux-observable'
import { concat, EMPTY, filter, merge, Observable, of } from 'rxjs'
import { debounceTime, mergeMap, withLatestFrom } from 'rxjs/operators'

import { mapStore, MapStore } from '@components/map'

type PartialRootState = {
  [addressStore.name]: AddressStore
  [navbarStore.name]: NavbarStore
  [mapStore.name]: MapStore
  [clusterStore.name]: ClusterStore
  [painterStore.name]: PainterStore
  [usersStore.name]: UsersStore
  [sessionStore.name]: SessionStore
  [planStore.name]: PlanStore
  [appStore.name]: AppStore
  [routerStore.name]: RouterStore
  [searchPannelMobileStore.name]: MobileSearchStore
}

const initialDataEpic = (
  action$: Observable<Action>,
  state$: StateObservable<PartialRootState>
) =>
  merge(
    action$.pipe(filter(sessionStore.actions.setSession.match)),
    action$.pipe(filter(sessionStore.actions.createSessionSuccess.match)),
    action$.pipe(filter(sessionStore.actions.updateSessionSuccess.match))
  ).pipe(
    debounceTime(500),
    withLatestFrom(state$),
    mergeMap(() => {
      const getPaymentSystems = of(planStore.actions.getPaymentMethodList())
      return concat(getPaymentSystems)
    })
  )

const navbarToggleEpic = (
  action$: Observable<Action>,
  state$: StateObservable<PartialRootState>
) =>
  action$.pipe(
    filter(navbarStore.actions.setPannelCollapsed.match),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const shoulPannelClose = action.payload
      if (!shoulPannelClose) {
        return EMPTY
      }
      const hasCluster = state.clusterSlice.selectedCluster
      const removeClusters = hasCluster
        ? of(clusterStore.actions.setSelectedCluster(null))
        : EMPTY

      return removeClusters
    })
  )

const addressClickEpic = (
  action$: Observable<Action>,
  state$: StateObservable<PartialRootState>
) =>
  action$.pipe(
    filter(addressStore.actions.setCurrentAddress.match),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const isNavbarClosed = state.navbarSlice.isPannelCollapsed
      const isMobileView = state.appSlice.isMobile

      const setPainter = of(
        painterStore.actions.setPainter({
          point: action.payload.getCoordinatesPair()
        })
      )
      const setPainterSize = of(
        painterStore.actions.setPainterSize({
          radius: MIN_PAINTER_SIZE
        })
      )
      const disablePaintMode = of(painterStore.actions.setPaintMode(false))

      if (!isMobileView) {
        console.log('clicked')
        const openNavbar = isNavbarClosed
          ? of(navbarStore.actions.setPannelCollapsed(false))
          : EMPTY
        return concat(setPainter, setPainterSize, disablePaintMode, openNavbar)
      }
      const closeSettingPage = of(
        searchPannelMobileStore.actions.setPannelCollapsed(true)
      )

      return concat(
        setPainter,
        setPainterSize,
        disablePaintMode,
        closeSettingPage
      )
    })
  )

const cityChangeEpic = (
  action$: Observable<Action>,
  state$: StateObservable<PartialRootState>
) =>
  action$.pipe(
    filter(addressStore.actions.setCurrentCity.match),
    withLatestFrom(state$),
    mergeMap(([_action, state]) => {
      const isNavbarOpened = !state.navbarSlice.isPannelCollapsed
      const isMobileView = state.appSlice.isMobile

      const selectedCluster = state.clusterSlice.selectedCluster
      const removeSelectedCluster = selectedCluster
        ? of(clusterStore.actions.setSelectedCluster(null))
        : EMPTY
      const totalCount = state.usersSlice.totalUsers
      const resetUsers = totalCount ? of(usersStore.actions.reset()) : EMPTY

      if (!isMobileView) {
        const closeNavbar = isNavbarOpened
          ? of(navbarStore.actions.setPannelCollapsed(true))
          : EMPTY
        return concat(removeSelectedCluster, closeNavbar, resetUsers)
      }
      const closeSettingsMenu = of(routerStore.actions.saveCurrent('/'))
      return concat(removeSelectedCluster, closeSettingsMenu, resetUsers)
    })
  )

const painterSetEpic = (
  action$: Observable<Action>,
  state$: StateObservable<PartialRootState>
) =>
  action$.pipe(
    filter(painterStore.actions.setPainter.match),
    withLatestFrom(state$),
    mergeMap(([_action, state]) => {
      const totalCount = state.usersSlice.totalUsers
      return totalCount ? of(usersStore.actions.reset()) : EMPTY
    })
  )

const clusterRemoveEpic = (
  action$: Observable<Action>,
  state$: StateObservable<PartialRootState>
) =>
  action$.pipe(
    filter(painterStore.actions.setPainter.match),
    withLatestFrom(state$),
    mergeMap(([_action, state]) => {
      const totalCount = state.usersSlice.totalUsers
      const isNavbarClosed = state.navbarSlice.isPannelCollapsed
      const resetUsers = totalCount ? of(usersStore.actions.reset()) : EMPTY
      const closeNavbar = isNavbarClosed
        ? EMPTY
        : of(navbarStore.actions.setPannelCollapsed(true))
      return concat(resetUsers, closeNavbar)
    })
  )

const mapClickEpic = (
  action$: Observable<Action>,
  state$: StateObservable<PartialRootState>
) =>
  action$.pipe(
    filter(mapStore.actions.mapClick.match),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const { latitude, longitude } = action.payload
      const isDrawingMode = state.mapSlice.isDrawingMode
      const isPaintStarted = state.painterSlice.isPaintStarted
      const isNavbarClosed = state.navbarSlice.isPannelCollapsed
      const selectedCluster = state.clusterSlice.selectedCluster

      const isMobileView = state.appSlice.isMobile

      const removeSelectedCluster = selectedCluster
        ? of(clusterStore.actions.setSelectedCluster(null))
        : EMPTY

      if (isDrawingMode) {
        if (isPaintStarted === false) {
          const setPainter = of(
            painterStore.actions.setPainter({
              point: { lat: latitude, long: longitude }
            })
          )
          if (isMobileView) {
            const disableDrawMode = of(
              mapStore.actions.toggleDrawingMode(false)
            )
            return concat(disableDrawMode, setPainter, removeSelectedCluster)
          }
          return concat(setPainter, removeSelectedCluster)
        }

        if (!isMobileView) {
          return concat(
            of(mapStore.actions.toggleDrawingMode(false)),
            isNavbarClosed
              ? of(navbarStore.actions.setPannelCollapsed(false))
              : EMPTY
          )
        }
        return concat(
          of(mapStore.actions.toggleDrawingMode(false)),
          of(searchPannelMobileStore.actions.setPannelCollapsed(false))
        )
      }
      if (!latitude || !longitude) {
        return EMPTY
      }
      const smallClusters = clusterStore.selectors.selectLittleClusters(state)
      const mediumClusters = clusterStore.selectors.selectMediumClusters(state)
      const largeClusters = clusterStore.selectors.selectLargeClusters(state)
      const clickedCluster = clusterLib.isClusterClicked(
        latitude,
        longitude,
        smallClusters,
        mediumClusters,
        largeClusters
      )
      const painterObject = state.painterSlice.painter

      // Если клик был по кластеру - делаем по нему поиск
      if (clickedCluster && !painterObject) {
        const setSelectedCluster = of(
          clusterStore.actions.setSelectedCluster(clickedCluster)
        )
        if (!isMobileView) {
          const openNavbar = isNavbarClosed
            ? of(navbarStore.actions.setPannelCollapsed(false))
            : EMPTY

          return concat(setSelectedCluster, openNavbar)
        }
        return concat(
          setSelectedCluster,
          of(searchPannelMobileStore.actions.setPannelCollapsed(false))
        )
      }

      const hasSelectedUsers = state.usersSlice.totalUsers > 0
      // Если был клик мимо кластера, то очищаем результаты поиска (кроме случаев, когда область задана пользователем)
      if (!clickedCluster && painterObject === null) {
        const resetUsers = hasSelectedUsers
          ? of(usersStore.actions.reset())
          : EMPTY

        if (!isMobileView) {
          const closeNavbar =
            isNavbarClosed === false
              ? of(navbarStore.actions.setPannelCollapsed(true))
              : EMPTY

          return concat(resetUsers, closeNavbar, removeSelectedCluster)
        }
        return concat(resetUsers, removeSelectedCluster)
      }
      return removeSelectedCluster
    })
  )

const removeUsersAfterRemovePainterEpic = (
  action$: Observable<Action>,
  state$: StateObservable<PartialRootState>
) =>
  action$.pipe(
    filter(painterStore.actions.resetPainter.match),
    withLatestFrom(state$),
    mergeMap(([_action, state]) => {
      const resetUsers = of(usersStore.actions.reset())
      const isNavbarClosed = state.navbarSlice.isPannelCollapsed
      const closeNavbar = isNavbarClosed
        ? EMPTY
        : of(navbarStore.actions.setPannelCollapsed(true))
      return concat(resetUsers, closeNavbar)
    })
  )

const closeMobileSearchAfterPaintStartEpic = (
  action$: Observable<Action>,
  state$: StateObservable<PartialRootState>
) =>
  action$.pipe(
    filter(mapStore.actions.toggleDrawingMode.match),
    withLatestFrom(state$),
    mergeMap(([_action, state]) => {
      const isDrawingMode = state.mapSlice.isDrawingMode
      if (!isDrawingMode) return EMPTY
      const isMobileView = state.appSlice.isMobile
      if (!isMobileView) return EMPTY

      return of(searchPannelMobileStore.actions.setPannelCollapsed(true))
    })
  )

const resetAllOnSignoutEpic = (
  action$: Observable<Action>,
  state$: StateObservable<PartialRootState>
) =>
  action$.pipe(
    filter(sessionStore.actions.removeSessionStart.match),
    withLatestFrom(state$),
    mergeMap(() => {
      const resetUsers = of(usersStore.actions.reset())
      const resetPainter = of(painterStore.actions.resetPainter())
      const resetCluster = of(clusterStore.actions.reset())
      const resetMap = of(mapStore.actions.reset())
      const resetLayers = of()
      const resetNavbar = of(navbarStore.actions.setPannelCollapsed(true))
      const resetSearchPannelMobile = of(
        searchPannelMobileStore.actions.setPannelCollapsed(true)
      )

      return concat(
        resetUsers,
        resetPainter,
        resetCluster,
        resetMap,
        resetLayers,
        resetNavbar,
        resetSearchPannelMobile
      )
    })
  )

registerEpic(initialDataEpic)
registerEpic(mapClickEpic)
registerEpic(painterSetEpic)
registerEpic(removeUsersAfterRemovePainterEpic)
registerEpic(navbarToggleEpic)
registerEpic(cityChangeEpic)
registerEpic(addressClickEpic)
registerEpic(clusterRemoveEpic)
registerEpic(closeMobileSearchAfterPaintStartEpic)
registerEpic(resetAllOnSignoutEpic)
